/**
 * Banks — directory page listing all 124 banks with their portfolio + NPA %.
 *
 * Layout:
 *   Page head + filter (date + search)
 *   KPI tiles  (banks reporting · system NPA % · GREEN/AMBER/RED counts)
 *   Sortable table — click row → /banks/:orgId
 *
 * The primary performance metric per row is NPA % (RBI-aligned health flag).
 * Date defaults to the richest snapshot via the API helper, so KPIs land on
 * the full historical portfolio (not the day's deltas).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import BankBadge from '../../components/BankBadge';
import Card from '../../components/Card';
import DatePicker from '../../components/DatePicker';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import Ico from '../../components/Ico';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import SortableTh from '../../components/SortableTh';
import { formatDate } from '../../helpers/dateFormat';
import { formatAbsCrore, formatCount, formatCrore, formatPct } from '../../helpers/format';
import { useSortable } from '../../hooks/useSortable';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchBankList, selectBankList } from '../../redux/slices/dashboard';
import type { BankListRow, Flag } from '../../type/api';

const FLAGS: Flag[] = ['GREEN', 'AMBER', 'RED', 'GREY'];

export default function BanksPage() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectBankList);

	const [params, setParams] = useSearchParams();
	const date = params.get('date');
	const [search, setSearch] = useState('');
	const [enabledFlags, setEnabledFlags] = useState<Set<Flag>>(new Set(FLAGS));

	useEffect(() => {
		dispatch(fetchBankList({ date: date ?? undefined }));
	}, [dispatch, date]);

	const data = cell.data;
	const banks: BankListRow[] = useMemo(
		() => (Array.isArray(data?.banks) ? data!.banks : []),
		[data],
	);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return banks.filter((b) => {
			if (!enabledFlags.has(b.npaFlag)) return false;
			if (!q) return true;
			if (String(b.orgId).includes(q)) return true;
			if ((b.orgName || '').toLowerCase().includes(q)) return true;
			return false;
		});
	}, [banks, search, enabledFlags]);

	const sort = useSortable<BankListRow>(filtered, { field: 'npaPct', dir: 'desc' });

	const setDate = (next: string) => {
		const p = new URLSearchParams(params);
		if (next) p.set('date', next);
		else p.delete('date');
		setParams(p, { replace: true });
	};
	const toggleFlag = (f: Flag) => {
		setEnabledFlags((prev) => {
			const next = new Set(prev);
			if (next.has(f)) {
				next.delete(f);
				if (next.size === 0) next.add(f); // keep at least one
			} else {
				next.add(f);
			}
			return next;
		});
	};

	const handleRefresh = () => dispatch(fetchBankList({ date: date ?? undefined }));

	const isLoading = cell.status === 'loading';

	const filterRow = (
		<div className="filterbar">
			<DatePicker
				value={date ?? data?.asOf ?? null}
				availableDates={data?.snapshotsAvailable}
				onChange={setDate}
				label="Snapshot"
			/>
			<div className="search" style={{ flex: '1 1 280px', maxWidth: 360 }}>
				<Ico name="search" size={12} />
				<label htmlFor="bank-list-search" style={{ position: 'absolute', left: -9999 }}>
					Search banks
				</label>
				<input
					id="bank-list-search"
					type="search"
					placeholder="Search by bank name or ID…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>
			<div role="group" aria-label="Filter by NPA flag" style={{ display: 'flex', gap: 6 }}>
				{FLAGS.map((f) => {
					const active = enabledFlags.has(f);
					return (
						<button
							key={f}
							type="button"
							className={`flag-toggle ${f.toLowerCase()}${active ? ' active' : ''}`}
							onClick={() => toggleFlag(f)}
							aria-pressed={active}
						>
							<span className="dot" aria-hidden="true" />
							{f}
						</button>
					);
				})}
			</div>
			<button
				type="button"
				className="btn ghost"
				onClick={handleRefresh}
				disabled={isLoading}
				style={{ marginLeft: 'auto' }}
			>
				Refresh
			</button>
		</div>
	);

	if (cell.status === 'loading' && !data) {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Banks</h1>
					</div>
				</div>
				{filterRow}
				<Card>
					<LoadingSkeleton rows={8} columns={8} />
				</Card>
			</div>
		);
	}
	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Banks</h1>
					</div>
				</div>
				{filterRow}
				<ErrorBanner message={cell.error || 'Failed to load banks'} onRetry={handleRefresh} />
			</div>
		);
	}
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head">
					<div>
						<h1 className="page-title">Banks</h1>
					</div>
				</div>
				{filterRow}
				<Card>
					<EmptyState title="No banks loaded" hint="Run the ETL to populate tblOrgSummary." />
				</Card>
			</div>
		);
	}

	const summary = data.summary;

	return (
		<div className="page-body">
			<div className="page-head">
				<div>
					<h1 className="page-title">Banks</h1>
					<div className="page-sub">
						All {summary.totalBanks} cooperative banks · primary metric is{' '}
						<strong style={{ color: 'var(--text)' }}>NPA %</strong> (RBI norm ≤ 7 %).
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
				</div>
			</div>

			{filterRow}

			<div className="grid cols-5">
				<KpiTile label="Banks reporting" value={formatCount(summary.totalBanks)} />
				<KpiTile
					label="System NPA %"
					value={formatPct(summary.systemNpaPct)}
					primary
				/>
				<KpiTile
					label="Healthy (≤ 7 %)"
					value={formatCount(summary.greenCount)}
					sub="GREEN"
				/>
				<KpiTile
					label="Watch (7–10 %)"
					value={formatCount(summary.amberCount)}
					sub="AMBER"
				/>
				<KpiTile
					label="At risk (> 10 %)"
					value={formatCount(summary.redCount)}
					sub="RED"
				/>
			</div>

			<div className="grid cols-3">
				<KpiTile label="Total Loans" value={formatCrore(summary.totalLoanCr, 2)} />
				<KpiTile label="Total Deposits" value={formatCrore(summary.totalDepositCr, 2)} />
				<KpiTile label="Total NPA" value={formatCrore(summary.totalNpaCr, 2)} />
			</div>

			<Card
				title={`All banks (${sort.rows.length}${
					sort.rows.length !== banks.length ? ` of ${banks.length}` : ''
				})`}
				tools={
					<span style={{ fontSize: 12, color: 'var(--text-faint)' }}>
						Click a row to drill into the bank
					</span>
				}
			>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="orgId" sort={sort} numeric>
									ID
								</SortableTh>
								<SortableTh field="orgName" sort={sort}>
									Bank
								</SortableTh>
								<SortableTh field="branchCount" sort={sort} numeric>
									Branches
								</SortableTh>
								<SortableTh field="loanCr" sort={sort} numeric>
									Loans ₹Cr
								</SortableTh>
								<SortableTh field="loanCount" sort={sort} numeric>
									Loan A/Cs
								</SortableTh>
								<SortableTh field="depositCr" sort={sort} numeric>
									Deposits ₹Cr
								</SortableTh>
								<SortableTh field="depositCount" sort={sort} numeric>
									Deposit A/Cs
								</SortableTh>
								<SortableTh field="npaCr" sort={sort} numeric>
									NPA ₹Cr
								</SortableTh>
								<SortableTh field="npaPct" sort={sort} numeric>
									NPA %
								</SortableTh>
								<SortableTh field="uniqueCustomers" sort={sort} numeric>
									Customers
								</SortableTh>
							</tr>
						</thead>
						<tbody>
							{sort.rows.map((b) => (
								<tr
									key={b.orgId}
									className="clickable"
									onClick={() => navigate(`/banks/${b.orgId}`)}
									tabIndex={0}
									onKeyDown={(e) => {
										if (e.key === 'Enter') navigate(`/banks/${b.orgId}`);
									}}
								>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{b.orgId}
									</td>
									<td>
										<BankBadge orgId={b.orgId} orgName={b.orgName} />
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatCount(b.branchCount)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatAbsCrore(b.loanCr)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatCount(b.loanCount)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatAbsCrore(b.depositCr)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatCount(b.depositCount)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatAbsCrore(b.npaCr)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatPct(b.npaPct)}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{formatCount(b.uniqueCustomers)}
									</td>
								</tr>
							))}
							{!sort.rows.length && (
								<tr>
									<td colSpan={10}>
										<EmptyState
											title="No banks match"
											hint="Try clearing the search or enabling more flag filters."
										/>
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>
		</div>
	);
}
